import React, { JSX } from 'react';
import './OrderDetailView.css';
import { BlechconOrder, BlechconOrderFile } from '../blechcon';
import { localized } from '../config/localization';
import { OrderTotal } from './OrderTotal/OrderTotal';
import { AddressComponent } from './AddressComponent';
import { formatDate } from './helper';
import { StatusBadge } from './StatusBadge/StatusBadge';

type Props = {
  readonly order: BlechconOrder;
  readonly children?: (({ file }: { file: BlechconOrderFile; index: number }) => React.ReactNode) | React.ReactNode;
};

export function OrderDetailView({ order, children }: Props): JSX.Element {
  return (
    <>
      <div className="order-detail-view">
        <div className="order-detail-view__info">
          <div className="order-detail-view__title">
            {localized.ORDER_ID}
            <span className="order-detail-view__title-normal">: {order.id}</span>
          </div>
          <div className="order-detail-view__row">
            {localized.APPROVAL_STATUS}:
            <div className="order-detail-view__row-badge">
              <StatusBadge orderStatus={order.orderStatus} />
            </div>
          </div>
          <div className="order-detail-view__title">
            {localized.DATE_ORDERED}
            <span className="order-detail-view__title-normal">: {formatDate(order.orderedAt)}</span>
          </div>
          <div className="order-detail-view__title">
            {localized.DATE_CREATED}
            <span className="order-detail-view__title-normal">: {formatDate(order.createdAt)}</span>
          </div>
          <div className="order-detail-view__title">
            {localized.DESIRED_DATE}
            <span className="order-detail-view__title-normal">: {formatDate(order.delivery?.desiredDate)}</span>
          </div>
          <div className="order-detail-view__title">
            {order.delivery?.selfPickup ? localized.PICKUP_MESSAGE : localized.DELIVERY_DATE}
            <span className="order-detail-view__title-normal">: {formatDate(order.delivery?.deliveryDate)}</span>
          </div>
          <div className="order-detail-view__title">
            {localized.CUSTOMER_REFERENCE}
            <span className="order-detail-view__title-normal">: {order.customerReference}</span>
          </div>
        </div>
        <AddressComponent
          address={order.delivery?.invoiceAddress}
          title={
            order.delivery?.invoiceAddress?.id === order.delivery?.shippingAddress?.id && !order.delivery?.selfPickup
              ? localized.INVOICE_AND_SHIPPING_ADDRESS
              : localized.INVOICE_ADDRESS
          }
          selfPickup={order.delivery?.selfPickup}
        />
        {order.delivery?.invoiceAddress?.id !== order.delivery?.shippingAddress?.id && !order.delivery?.selfPickup ? (
          <AddressComponent address={order.delivery?.shippingAddress} title={localized.SHIPPING_ADDRESS} />
        ) : null}
      </div>
      <table className="table">
        <thead>
          <tr>
            <td className="table-cell__product">{localized.PRODUCT}</td>
            <td>{localized.QUANTITY}</td>
            <td>{localized.UNIT_PRICE} (EUR)</td>
            <td className="table-cell--right">{localized.TOTAL} (EUR)</td>
          </tr>
        </thead>
        <tbody className="no-hover">
          {order.files.map((file, index) => {
            return typeof children === 'function'
              ? children({ file, index })
              : React.Children.map(children, (child) => child);
          })}
        </tbody>
      </table>
      <OrderTotal
        totalPrice={order.prices.totalPrice || 0}
        materialTotalPrice={order.prices.materialTotalPrice || 0}
        shippingCost={order.prices.shippingCost}
        selfPickup={order.delivery?.selfPickup || false}
        discountPrice={order.prices.discountPrice || 0}
      />
    </>
  );
}
